import React, { lazy, Suspense, useSyncExternalStore } from 'react'
import { Navigate, createBrowserRouter } from 'react-router-dom'

import App from '@/App'
import { Empty } from 'antd'
import { canAccessSystemUserPage, hasPermission, getAuthSnapshot, subscribeAuth } from '@/utils/auth'
const ProductManage = lazy(() => import('@/pages/gateway/ProductManage'))
const ProductDetail = lazy(() => import('@/pages/gateway/ProductManage/Detail'))
const DeviceManage = lazy(() => import('@/pages/gateway/DeviceManage'))
const DeviceDetail = lazy(() => import('@/pages/gateway/DeviceManage/Detail'))
const DeviceConfig = lazy(() => import('@/pages/gateway/DeviceConfig'))
const DeviceDebug = lazy(() => import('@/pages/gateway/DeviceDebug'))
const Dashboard = lazy(() => import('@/pages/home/Dashboard'))
const AlarmCenter = lazy(() => import('@/pages/monitor/AlarmCenter'))
const SystemMonitor = lazy(() => import('@/pages/monitor/SystemMonitor'))
const Login = lazy(() => import('@/pages/auth/login/index'))
const GlobalDashboard = lazy(() => import('@/pages/dashboard').then(module => ({ default: module.GlobalDashboard })))

const DeviceGroup = lazy(() => import('@/pages/gateway/DeviceGroup'))
const GNSSControl = lazy(() => import('@/pages/gateway/GNSSControl'))
const OTAManage = lazy(() => import('@/pages/gateway/OTA'))
const UserManage = lazy(() => import('@/pages/system/Tenant'))
const UserAccountManage = lazy(() => import('@/pages/system/UserAccount'))
const ProfilePage = lazy(() => import('@/pages/profile'))
const AuthorizationPage = lazy(() => import('@/pages/system/Authorization'))
const ForbiddenPage = lazy(() => import('@/pages/403'))

const AdminGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  return canAccessSystemUserPage() ? children : <Navigate to="/403" replace />
}

const PlatformGuard: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  return hasPermission('*', '*') ? children : <Navigate to="/403" replace />
}

const PermissionRoute: React.FC<{ resource: string; action: string; children: React.ReactElement }> = ({ resource, action, children }) => {
  useSyncExternalStore(subscribeAuth, getAuthSnapshot)
  return hasPermission(resource, action) ? children : <Navigate to="/403" replace />
}

const RuleDetail = lazy(() => import('@/pages/monitor/AlarmCenter/Detail'))

const router = createBrowserRouter([
  {
    path: '/auth/login',
    element: <Suspense fallback={null}><Login /></Suspense>,
  },
  {
    path: '/',
    element: <App />,
    children: [
      {
        path: '/gateway/product/manage',
        element: <PermissionRoute resource="product" action="read"><ProductManage /></PermissionRoute>,
      },
      {
        path: '/gateway/product/detail/:id',
        element: <PermissionRoute resource="product" action="read"><ProductDetail /></PermissionRoute>,
      },
      {
        path: '/gateway/device/manage',
        element: <PermissionRoute resource="device" action="read"><DeviceManage /></PermissionRoute>,
      },
      {
        path: '/gateway/device/manage/:id',
        element: <PermissionRoute resource="device" action="read"><DeviceManage /></PermissionRoute>,
      },
      {
        path: '/gateway/device/detail/:id',
        element: <PermissionRoute resource="device" action="read"><DeviceDetail /></PermissionRoute>,
      },
      {
        path: '/gateway/device/config/:id',
        element: <PermissionRoute resource="device" action="read"><PermissionRoute resource="dashboard" action="read"><DeviceConfig /></PermissionRoute></PermissionRoute>,
      },
      {
        path: '/gateway/device/debug/:id',
        element: <PermissionRoute resource="debug" action="read"><PermissionRoute resource="debug" action="execute"><DeviceDebug /></PermissionRoute></PermissionRoute>,
      },
      {
        path: '/home/dashboard',
        element: <Dashboard />,
      },
      {
        path: '/monitor/alarmCenter/list',
        element: <PermissionRoute resource="alert" action="read"><AlarmCenter /></PermissionRoute>,
      },
      {
        path: '/monitor/systemMonitor',
        element: <PermissionRoute resource="log" action="read"><SystemMonitor /></PermissionRoute>,
      },
      {
        path: '/monitor/alarmCenter/detail/:id',
        element: (
          <PermissionRoute resource="alert" action="read">
            <Suspense fallback={null}><RuleDetail /></Suspense>
          </PermissionRoute>
        ),
      },
      // 数据大屏（科幻风格）
      {
        path: '/dashboard/screens',
        element: <PermissionRoute resource="dashboard" action="read"><GlobalDashboard /></PermissionRoute>,
      },
      {
        path: '/gateway/device/group',
        element: <PermissionRoute resource="device" action="read"><DeviceGroup /></PermissionRoute>,
      },
      {
        path: '/gateway/gnss/control',
        element: <PermissionRoute resource="device" action="execute"><GNSSControl /></PermissionRoute>,
      },
      {
        path: '/gateway/ota',
        element: <PermissionRoute resource="ota" action="read"><OTAManage /></PermissionRoute>,
      },
      // 个人中心
      {
        path: '/profile',
        element: <ProfilePage />,
      },
      {
        path: '/system/user',
        element: <PlatformGuard><UserManage /></PlatformGuard>,
      },
      {
        path: '/system/user-account',
        element: <AdminGuard><UserAccountManage /></AdminGuard>,
      },
      {
        path: '/system/authorization',
        element: <AuthorizationPage />,
      },
      {
        path: '/403',
        element: <ForbiddenPage />,
      },
      // 兼容旧路由
      {
        path: '/system/tenant',
        element: <PlatformGuard><UserManage /></PlatformGuard>,
      },
    ],
  },
  {
    path: '*',
    element: <Empty description={'404 Not Found'} style={{ marginTop: '300px' }}/>,
  },
])

export default router
